import { CommonSetType } from '..';

type SwitchFunctions<T> = {
    onAdmitted: () => T;
    onEAndS: () => T;
    onNfip: () => T;
};

export type RatingTypeType = CommonSetType<typeof RatingType>;

export class RatingType {
    readonly id: number;
    readonly abbreviation: string;
    readonly value: string;
    readonly displayValue: string;

    private constructor(id: number, abbreviation: string, value: string, displayValue?: string) {
        this.id = id;
        this.value = value;
        this.abbreviation = abbreviation;
        this.displayValue = displayValue || value;
    }

    /** Admitted business (i.e. not excess and surplus insurance). */
    public static readonly Admitted = new RatingType(1, 'AD', 'Admitted');

    /** Excess and surplus lines insurance. */
    public static readonly EAndS = new RatingType(2, 'ES', 'EAndS', 'E&S');

    /** A RatingType that is near impossible to be used with a Quote. Primarily used as a second excel rater and needed for seeding data.
     * There are no quotes in any database (prod, beta, dev) with this RatingType. This is here because it exists but will likely never be used.
     */
    public static readonly Adequacy = new RatingType(3, 'RA', 'Adequacy');

    /** NFIP is neither admitted nor E&S because it is US government insurance. */
    public static readonly Nfip = new RatingType(4, 'NF', 'NFIP');

    public switch<T>({ onAdmitted, onEAndS, onNfip }: SwitchFunctions<T>): T {
        switch (this) {
            case RatingType.Admitted:
                return onAdmitted();
            case RatingType.EAndS:
                return onEAndS();
            case RatingType.Nfip:
                return onNfip();
            default:
                throw new Error(`RatingType case not implemented: ${this}`);
        }
    }
}

export namespace RatingType {
    export function all(): RatingType[] {
        return [RatingType.Admitted, RatingType.EAndS, RatingType.Nfip];
    }

    export function parseOrDefault(value: string | number | undefined | null): RatingType | null {
        return (
            all().find(ratingType => {
                if (typeof value === 'number') return ratingType.id === value;
                return stringMatchesProperty(ratingType);
            }) || null
        );

        function stringMatchesProperty(ratingType: RatingType) {
            if (typeof value !== 'string') return false;

            const properties = [
                    ratingType.id.toString(),
                    ratingType.value,
                    ratingType.abbreviation,
                    ratingType.displayValue,
                ],
                normalizedValue = value.toLowerCase();

            return properties.some(matcher => matcher.toLowerCase() === normalizedValue);
        }
    }

    export function parse(value: string | number | undefined | null): RatingType {
        const result = parseOrDefault(value);
        if (!result) {
            throw new Error(`Could not parse any RatingType from the value: ${value}`);
        }

        return result;
    }
}
